import { IStockInput } from "../../../domain/IStockInput";
import { DiffDays, FormatDateShort } from "utils/dateUtils";
import { buildAuditTooltip } from "utils/auditUtil";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { RootState } from "reducers";
import { useSelector } from "react-redux";
import { useAppDispatch } from "hooks/hooks";
import {
  setSelectedInputId,
  setOutputValue,
  setDeleteInputModalOpen,
  setUpdateInputItemPopover,
  updateStockInput,
  setSelectedReservationId,
} from "../inputSlice";
import { toast } from "react-toastify";
import { FormatNumber, FormatNumberOrNull, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import { grey } from "@mui/material/colors";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoTooltip from "components/Popup/InfoTooltip";
import { IStockInputUpdate } from "Dashboard/scenes/Stock/domain/IStockInputUpdate";
import { debounce } from "lodash";
import PartnerSimpleSelect from "../../../../Partners/components/PartnerSimpleSelect";
import { styles } from "Dashboard/components/SelectStyle";
import { IOption } from "Dashboard/domain/IOption";
import { copyToClipboard } from "utils/clipboardUtils";
import NumberInput from "Dashboard/components/NumberInput";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { STOCK_MODULE_NAME } from "Dashboard/scenes/Stock/domain/StockModule";
import { useReduxField } from "hooks/useReduxField";
import { debounceTimeout } from "utils/debounceUtil";

interface IProps {
  input: IStockInput;
  partnerOptions: IOption[];
  index: number;
  reservationId?: number;
  reservedQuantityAmount?: number;
  lastTableElementRef?: any;
};

const StockInput = ({
  input,
  partnerOptions,
  index,
  reservationId,
  reservedQuantityAmount,
  lastTableElementRef
}: IProps) => {
  // Narrow selectors: each returns a primitive so a row only re-renders when
  // its own relevant state changes, not on every input-slice mutation.
  const isSelected = useSelector(
    (state: RootState) => state.stock.input.selectedInputId === input.inputId);
  const selectedReservationId = useSelector(
    (state: RootState) => state.stock.input.selectedReservationId);
  const valueForOutput = useSelector(
    (state: RootState) => state.stock.input.outputValues.map[input.inputId]);
  const outputRepositoryId = useSelector(
    (state: RootState) => state.stock.input.outputValues.repository?.repositoryId);
  const { user } = useSelector(selectStateAuth);
  const dispatch = useAppDispatch();

  // The supplier react-select is expensive; mount it only after the user
  // activates the cell. Until then we render a lightweight read-only label.
  const [showPartnerSelect, setShowPartnerSelect] = useState(false);

  const hasRemainingQuantity = Math.round(input.quantityAmountRemaining * 1000) > 0;
  const pricePerUnitNotSet
    = input.pricePerUnit === null
    || input.pricePerUnit === undefined
    || input.pricePerUnit === 0;
  const inputIsNotNew = -DiffDays(input.date) > 14;
  const canSetValueForOutput = hasRemainingQuantity
    && input.billOfLandingId?.toLowerCase() !== 'obrisati'
    && hasWriteAccessTo(user, STOCK_MODULE_NAME);

  const valueForOutputAsString = typeof valueForOutput === "number" ? valueForOutput.toString() : "";
  const [localQuantity, setLocalQuantity] = useState(valueForOutputAsString);
  useEffect(() => {
    setLocalQuantity(valueForOutputAsString);
  }, [valueForOutputAsString]);

  const commitQuantityDebounced = useMemo(
    () =>
      debounce((value: string) => {
        dispatch(
          setOutputValue({
            key: input.inputId,
            repository: input.repository,
            value: value && !Number.isNaN(Number(value)) ? Number(value) : undefined,
          })
        );
      }, 500),
    [dispatch, input]
  );

  const [warningMsg, setWarningMsg] = useState('');

  const onChangeQuantityForOutput = (value: string) => {
    if (!isValidQuantityForOutput()) return;
    setLocalQuantity(value);
    commitQuantityDebounced(value);
  };

  const isValidQuantityForOutput = () => {
    if (outputRepositoryId === undefined) return true;

    if (outputRepositoryId !== input.repository.repositoryId) {
      setWarningMsg("Artikli moraju biti iz istog magacina!");
      return false;
    }

    return true;
  }

  useEffect(() => {
    if (warningMsg) {
      toast.warning(warningMsg);
      setWarningMsg('');
    }
  }, [warningMsg]);

  const onBlurQuantityForOutput = () => {
    if (!localQuantity) return;

    let currentValue = Number(localQuantity);
    const remainingValue = input.quantityAmountRemaining;

    if (currentValue > remainingValue) {
      const overflow = Math.round((currentValue - remainingValue) * 100) / 100;
      toast.info(
        `Nema ${currentValue} u magacinu. Upisujem ${remainingValue}, a nedostajućih ${overflow} upisujem u klipboard.`
      );
      copyToClipboard(overflow.toString());
      currentValue = remainingValue;
      setLocalQuantity(currentValue.toString());
    }

    commitQuantityDebounced(currentValue.toString());
  };

  const receiptNumberChangedRef = useRef(false);
  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IStockInputUpdate, showToast = true) => {
      try {
        await dispatch(updateStockInput(data)).unwrap();
        if (showToast) {
          toast.success("Izmena je uspešno zabeležena.");
          receiptNumberChangedRef.current = false;
        }
      } catch (status) {
        if (status === 403 || status === 400) {
          toast.error("Neočekivana greška");
        } else {
          toast.error("Nepredviđena greška");
        }
      }
    }, debounceTimeout),
    [dispatch]
  );

  const [receiptNumber, setReceiptNumber] = useReduxField<string, IStockInputUpdate>(
    input.receiptNumber,
    (value) => ({
      inputId: input.inputId,
      receiptNumber: value,
    }),
    (data) => {
      const showToast = receiptNumberChangedRef.current;
      updateBasicFieldWithDelay(data, showToast);
    }
  );

  const onReceiptNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    receiptNumberChangedRef.current = true;
    setReceiptNumber(e.target.value);
  };



  const onChangeBasicField = (data: IStockInputUpdate) => {
    updateBasicFieldWithDelay(data);
  };

  const onChangePartner = async (newPartnerId: number) => {
    onChangeBasicField({ inputId: input.inputId, supplierPartnerId: newPartnerId });
  };

  // Audit info (created/updated by, when) shown on hover over the date.
  const dateTooltip = buildAuditTooltip("", input);

  return (
    <>
      <div
        className={`row ${isSelected ? " active" : ""}`}
        key={input.inputId}
        onClick={() => {
          dispatch(setSelectedInputId(input.inputId));
          if (hasWriteAccessTo(user, STOCK_MODULE_NAME) && reservationId !== undefined) {
            dispatch(setSelectedReservationId(reservationId));
          }
        }
        }
        ref={lastTableElementRef}
      >
        <div className="col">
          <div className="bg-item bg-transparent center">
            <span className="xxx">
              <input type="checkbox" checked={input.xxx} readOnly />
              <span className="special-ch"></span>
            </span>
          </div>
        </div>
        <div className="col">
          <input type="text" className={`bg-item ${!input.receiptNumber ? 'bg-danger' : 'bg-item-border-light'}
            ${hasWriteAccessTo(user, STOCK_MODULE_NAME) ? ' hoverBorder' : ''} center`}
            value={receiptNumber}
            onChange={hasWriteAccessTo(user, STOCK_MODULE_NAME) ? onReceiptNumberChange : undefined}
            readOnly={!hasWriteAccessTo(user, STOCK_MODULE_NAME)}
          />
        </div>
        <div className="col">
          <input className={`bg-item bg-item-low-importance center ${hasWriteAccessTo(user, STOCK_MODULE_NAME) ? "hoverBorder" : ""}`}
            type="text"
            defaultValue={input.billOfLandingId}
            onChange={hasWriteAccessTo(user, STOCK_MODULE_NAME)
              ? (e) => onChangeBasicField({ inputId: input.inputId, billOfLandingId: e.target.value })
              : undefined}
            readOnly={!hasWriteAccessTo(user, STOCK_MODULE_NAME)}
          />
        </div>

        <div className="col strong">
          <div className={`bg-item overflowMinWidth center${hasWriteAccessTo(user, STOCK_MODULE_NAME) && reservationId === undefined ? ' hoverBorder' : ''}`}
            onClick={hasWriteAccessTo(user, STOCK_MODULE_NAME) && reservationId === undefined
              ? (e) => {
                dispatch(
                  setUpdateInputItemPopover({
                    isOpen: true,
                    left: e.clientX,
                    top: e.clientY,
                    itemRowIndex: index,
                    focus: "itemCode",
                  })
                )
              }
              : undefined}>
            <InfoTooltip
              classesProp="bg-text-overflow"
              tooltip={input.itemCode}
              element="span">
              {input.itemCode}
            </InfoTooltip>
          </div>
        </div>

        <div className="col center uppercase">
          <div className="bg-item bg-item-border-light danger">
            <span >{input.repository.shortName}</span>
          </div>
        </div>
        <div className="col uppercase">
          <div className={`bg-item overflowMinWidth ${hasRemainingQuantity ? "strong" : "light"}`}>
            <InfoTooltip
              tooltip={input.item.name}
              element="span"
              classesProp="bg-text-overflow"
            >
              {input.item.name}
            </InfoTooltip>
          </div>
        </div>
        <div className="col center strong">
          {reservationId === undefined || reservationId === selectedReservationId
            ? <NumberInput
              formatAs="quantity"
              value={localQuantity}
              onChange={(_: string, unmaskedValue: string) => onChangeQuantityForOutput(unmaskedValue)}
              onBlur={onBlurQuantityForOutput}
              className={`bg-item-fix danger${canSetValueForOutput ? ' hoverBorder' : ''}`}
              disabled={!canSetValueForOutput}
            />
            : <span className="bg-item-fix quantity-for-output">{FormatNumberOrNull(reservedQuantityAmount, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
          }
        </div>
        <div className="col">
        </div>
        <div className="col">
          {hasWriteAccessTo(user, STOCK_MODULE_NAME)
            ? (showPartnerSelect
              ? <div className="bg-item bg-item-low-importance">
                <PartnerSimpleSelect
                  customStyles={styles.dropdownInTable}
                  defaultValue={input.supplier?.partnerId}
                  options={partnerOptions}
                  onChange={onChangePartner}
                  autoFocus
                  openMenuOnFocus
                />
              </div>
              : <div
                className="bg-item bg-item-low-importance overflowMinWidth hoverBorder"
                onClick={() => setShowPartnerSelect(true)}>
                <span className="bg-text-overflow">
                  {input.supplier?.shortName}
                </span>
              </div>)
            : (
              <InfoTooltip tooltip={input.supplier.fullName}
                classesProp="bg-item light overflowMinWidth">
                <span className="bg-text-overflow">
                  {input.supplier.shortName}
                </span>
              </InfoTooltip>
            )}
        </div>
        <div className="col strong">
          <div className="bg-item right">
            <span className="danger">
              {FormatNumber(input.quantityAmountRemaining)}
            </span>
          </div>
        </div>
        <div className="col">
          <div className="bg-item bg-transparent">
            <span>{input.quantityUnit.name}</span>
          </div>
        </div>
        <div className="col">
          <div className="bg-item overflowMinWidth right">
            <span className="bg-text-overflow">
              {FormatNumberWithUnitOrNull(input.remainingTotalValue, "rsd")}
            </span>
          </div>
        </div>
        <div className="col">
          <div className="bg-item right">
            <span>{FormatNumber(input.quantityAmountTotal)}</span>
          </div>
        </div>
        <div className="col">
          <InfoTooltip
            tooltip={FormatNumberWithUnitOrNull(input.pricePerUnit, "rsd") || ""}
            classesProp={`bg-item right overflowMinWidth ${hasWriteAccessTo(user, STOCK_MODULE_NAME) && reservationId === undefined ? " hoverBorder" : ""}${pricePerUnitNotSet && inputIsNotNew ? " bg-danger" : ""}`}
            onClick={hasWriteAccessTo(user, STOCK_MODULE_NAME) && reservationId === undefined
              ? (e) => {
                dispatch(
                  setUpdateInputItemPopover({
                    isOpen: true,
                    left: e.clientX,
                    top: e.clientY,
                    itemRowIndex: index,
                    focus: "pricePerUnit",
                  })
                )
              }
              : undefined}>
            <span className="bg-text-overflow">
              {FormatNumberWithUnitOrNull(input.pricePerUnit, 'rsd')}
            </span>
          </InfoTooltip>
        </div>

        <div className="col">
          <div className={`bg-item right
            ${hasWriteAccessTo(user, STOCK_MODULE_NAME) ? "hoverBorder" : ""}`}>
            <NumberInput
              formatAs="rsd"
              onChange={(_: string, __: string, parsedValue: number) => {
                if (parsedValue !== input.deliveryPrice) {
                  onChangeBasicField({ inputId: input.inputId, deliveryPrice: parsedValue });
                }
              }}
              defaultValue={`${input.deliveryPrice}`}
              disabled={!hasWriteAccessTo(user, STOCK_MODULE_NAME)}
            />
          </div>
        </div>
        <div className="col">
          <div className="bg-item overflowMinWidth right">
            <span className="bg-text-overflow">
              {FormatNumberWithUnitOrNull(input.initialTotalValue, "rsd")}
            </span>
          </div>
        </div>
        <div className="col light">
          <div className="bg-item center">
            <InfoTooltip
              tooltip={dateTooltip}
              element="span"
            >
              {FormatDateShort(input.date)}
            </InfoTooltip>
          </div>
        </div>
        <div className="col center">
          {!input.isUsedAnywhere && hasFullAccessTo(user, STOCK_MODULE_NAME) && reservationId === undefined
            ? (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(
                    setDeleteInputModalOpen({ visible: true, index: index })
                  );
                }}
              >
                <CancelIcon
                  style={{ cursor: "pointer", fontSize: 15, color: grey[300] }}
                />
              </span>
            )
            : (<></>)}
        </div>
        <div className="col light">
          <div className="bg-item bg-transparent">
            <span>{input.broughtBy?.name}</span>
          </div>
        </div>
        <div className="col">
          <input type="text"
            className={`bg-item bg-transparent light${hasWriteAccessTo(user, STOCK_MODULE_NAME) ? ' hoverBorder' : ''}`}
            defaultValue={input.note}
            onChange={(e) => onChangeBasicField({ inputId: input.inputId, note: e.target.value })}
            disabled={!hasWriteAccessTo(user, STOCK_MODULE_NAME)}
          />
        </div>
      </div >
    </>
  );
};

// Memoized so a row is not re-rendered when TopSection re-renders for reasons
// unrelated to this row (its `input` reference is preserved by immer when the
// row's data is unchanged). Row-specific updates still flow via the narrow
// selectors above.
export default memo(StockInput);
